import { ABTestVariationEnum } from '@inter-ikea-kompis/enums';
/**
 * A/B test variation utility for kiosk.
 *
 * Kiosk A/B tests have a separate way of calculating variations, based on the assumption that kiosks can be used for several days without being restarted. In order not to skew the amount of users being exposed to the A vs the B version too much by a popular kiosk not being restarted for a long time, a new A/B version will be generated each day.
 *
 * The variation is based on is based on the kiosk ID and the current date using a seeded random number generator. In that way the same kiosk will have the same variation through the whole day, but the following day will have a newly generated variation with a 50/50 chance between each alternative.
 */
export default class KioskABTestVariation {
    static getVariation(kioskLongTermId) {
        return this.getAB(kioskLongTermId);
    }
    /**
     * Generates a hash consisting of 4 32 bit integers from a string.
     *
     * Inspired by https://stackoverflow.com/a/47593316.
     */
    static cyrb128(str) {
        let h1 = 1779033703;
        let h2 = 3144134277;
        let h3 = 1013904242;
        let h4 = 2773480762;
        for (let i = 0, k; i < str.length; i++) {
            k = str.charCodeAt(i);
            h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
            h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
            h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
            h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
        }
        h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
        h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
        h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
        h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        (h1 ^= h2 ^ h3 ^ h4), (h2 ^= h1), (h3 ^= h1), (h4 ^= h1);
        return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
    }
    /**
     * Takes a 128 bit seed consisting of 4 32 bit integers and returns a function
     * that generates pseudo random deterministic 32 bit integers.
     *
     * @returns A function that generates pseudo random deterministic 32 bit integers.
     *
     * Inspired by https://stackoverflow.com/a/47593316.
     * See test made https://i-p-e-x.atlassian.net/browse/KOMPIS-5901?focusedCommentId=202004.
     */
    static sfc32(a, b, c, d) {
        return function () {
            a |= 0;
            b |= 0;
            c |= 0;
            d |= 0;
            const t = (((a + b) | 0) + d) | 0;
            d = (d + 1) | 0;
            a = b ^ (b >>> 9);
            b = (c + (c << 3)) | 0;
            c = (c << 21) | (c >>> 11);
            c = (c + t) | 0;
            return (t >>> 0) / 4294967296;
        };
    }
    static getAB(longTermId) {
        const currentDate = new Date();
        const seed = this.cyrb128(longTermId + currentDate.getDate() + currentDate.getMonth() + currentDate.getFullYear());
        const rand = this.sfc32(...seed);
        if (rand() > 0.5) {
            return ABTestVariationEnum.A;
        }
        return ABTestVariationEnum.B;
    }
}
//# sourceMappingURL=KioskABTestVariation.js.map